// Analytics chart: locations x games through the day. Same look and controls as the Overview
// comparison chart, shown three ways ("Split by"):
// - One chart: every line together; each location has a color and each game a line style.
// - By game: a small chart per game, lines colored by location.
// - By location: a small chart per location, lines colored by game (like the Overviews).
// The small charts share one y-axis so they can be compared at a glance.
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart as ChartJS } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  ChartHeader,
  ChartSegmented,
  applyChartTheme,
  sectionLineDataset,
  hoverGuidePlugin,
  CHART_THEME,
} from '../Concession/charts/chartTheme';
import { minuteToClock } from './analyticsMetrics';

ChartJS.register(annotationPlugin);

const offsetLabel = (v, long = false) => {
  if (v === 0) return long ? 'Game start' : 'Start';
  const sign = v > 0 ? '+' : '−';
  const h = Math.floor(Math.abs(v) / 60);
  const m = Math.abs(v) % 60;
  if (!long) return m === 0 ? `${sign}${h}h` : '';
  return `${sign}${h ? `${h}h ` : ''}${m ? `${m}m` : ''}`.trim() + ' from start';
};

// A short line in the game's style, for the legend and the game chips. It always shows the pattern
// repeating (at least two full cycles, ending on a whole cycle); a long pattern is scaled down to fit.
// Flat line ends, like the chart lines, so short gaps don't close up.
const SAMPLE_WIDTH = 44;
export const DashSample = ({ dash, color = 'currentColor' }) => {
  const pattern = dash && dash.length ? dash : null;
  let width = SAMPLE_WIDTH;
  let scaled = null;
  if (pattern) {
    const cycle = pattern.reduce((a, b) => a + b, 0);
    const scale = Math.min(1, SAMPLE_WIDTH / (2 * cycle));
    const cycles = Math.max(2, Math.floor(SAMPLE_WIDTH / (cycle * scale)));
    scaled = pattern.map((d) => Math.round(d * scale * 100) / 100);
    // End just after the last cycle's final dash, so it doesn't look cut off
    width = Math.round(cycles * cycle * scale - scaled[scaled.length - 1]);
  }
  return (
    <svg width={width} height="8" viewBox={`0 0 ${width} 8`} aria-hidden="true" className="an-dash">
      <line x1="0" y1="4" x2={width} y2="4" stroke={color} strokeWidth="2.25" strokeLinecap="butt" strokeDasharray={scaled ? scaled.join(' ') : undefined} />
    </svg>
  );
};

// A y-axis top every chart can share: a round number just above the highest value
const niceMax = (value) => {
  if (!(value > 0)) return undefined;
  const raw = value * 1.08;
  const step = 10 ** Math.floor(Math.log10(raw));
  const n = raw / step;
  const nice = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((x) => n <= x);
  return nice * step;
};

const buildOptions = ({ align, yTitle, formatValue, formatTick, yMax, xRange, compact }) => {
  const base = applyChartTheme({
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    scales: {
      x: {
        type: 'linear',
        ...(xRange ? { min: xRange.min, max: xRange.max } : {}),
        ticks: {
          stepSize: 60,
          callback: (v) => (align === 'start' ? offsetLabel(v) : (v % 60 === 0 ? minuteToClock(v).replace(':00', '') : '')),
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ...(yMax != null ? { max: yMax } : {}),
        title: { display: !compact, text: yTitle },
        ticks: { precision: 0, ...(formatTick ? { callback: formatTick } : {}) },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const x = items[0]?.parsed?.x;
            if (x == null) return '';
            return align === 'start' ? offsetLabel(x, true) : minuteToClock(x);
          },
          label: (item) => ` ${item.dataset.label}: ${formatValue(item.parsed.y)}`,
        },
      },
      annotation: {
        annotations: align === 'start'
          ? [{
              type: 'line',
              scaleID: 'x',
              value: 0,
              borderColor: CHART_THEME.gameStart,
              borderWidth: 1.5,
              borderDash: [6, 4],
              label: {
                display: !compact,
                content: 'Game start',
                position: 'start',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                color: CHART_THEME.gameStart,
                font: { family: CHART_THEME.font, size: 11, weight: '600' },
                padding: { x: 6, y: 2 },
              },
            }]
          : [],
      },
    },
  });
  // Lines share one x grid, so the hovered time lists one value per line
  base.interaction = { mode: 'nearest', axis: 'x', intersect: false };
  return base;
};

const toDatasets = (lines, { colorOf, dashOf, labelOf }) => ({
  datasets: lines.map((l) => ({
    label: labelOf(l),
    data: l.points,
    ...sectionLineDataset(colorOf(l)),
    fill: false,
    borderWidth: 2.25,
    borderDash: dashOf(l),
  })),
});

export default function AnalyticsCompareChart({
  title,
  subtitle,
  metricOptions,
  metric,
  onMetric,
  align,
  onAlign,
  alignLocked,
  resolution,
  onResolution,
  extraControls = null,
  splitBy = 'none',
  onSplitBy,
  lines,
  subjects, // [{ key, name, color }]
  games, // [{ key, label, dash, color }] (empty when games are averaged)
  unit,
  yTitle,
  formatValue = (v) => `${v} ${unit}`,
  formatTick,
}) {
  // Which splits make sense: by game needs 2+ games shown separately, by location 2+ locations
  const splitOptions = [
    { value: 'none', label: 'One chart' },
    ...(games.length > 1 ? [{ value: 'game', label: 'By game' }] : []),
    ...(subjects.length > 1 ? [{ value: 'location', label: 'By location' }] : []),
  ];
  const split = splitOptions.some((o) => o.value === splitBy) ? splitBy : 'none';

  const gameByKey = useMemo(() => new Map(games.map((g) => [g.key, g])), [games]);
  const subjectByKey = useMemo(() => new Map(subjects.map((s) => [s.key, s])), [subjects]);

  // Small charts: one per game or per location, in the order they were picked
  const panels = useMemo(() => {
    if (split === 'game') {
      return games.map((g) => ({
        key: g.key,
        title: g.label.long,
        sub: g.label.sub,
        swatch: <DashSample dash={[]} color={g.color} />,
        lines: lines.filter((l) => l.gameKey === g.key),
      }));
    }
    if (split === 'location') {
      return subjects.map((s) => ({
        key: s.key,
        title: s.name,
        sub: null,
        swatch: <span className="cx-ov-swatch" style={{ background: s.color }} />,
        lines: lines.filter((l) => l.subjectKey === s.key),
      }));
    }
    return [];
  }, [split, games, subjects, lines]);

  const yMax = useMemo(
    () => (split === 'none' ? undefined : niceMax(Math.max(0, ...lines.flatMap((l) => l.points.map((p) => p.y))))),
    [split, lines],
  );

  // Same time axis on every small chart (a game still in progress would otherwise get a shorter one)
  const xRange = useMemo(() => {
    if (split === 'none') return null;
    const xs = lines.flatMap((l) => l.points.map((p) => p.x));
    return xs.length ? { min: Math.min(...xs), max: Math.max(...xs) } : null;
  }, [split, lines]);

  const options = useMemo(
    () => buildOptions({ align, yTitle, formatValue, formatTick, yMax, xRange, compact: split !== 'none' }),
    [align, yTitle, formatValue, formatTick, yMax, xRange, split],
  );

  // One chart: location colors, game line styles. By game: location colors, solid.
  // By location: game colors, solid (one color per game says it already).
  const oneChartData = useMemo(() => toDatasets(lines, {
    colorOf: (l) => l.color,
    dashOf: (l) => l.dash || [],
    labelOf: (l) => l.label,
  }), [lines]);
  const panelData = (panel) => toDatasets(panel.lines, split === 'game'
    ? { colorOf: (l) => l.color, dashOf: () => [], labelOf: (l) => subjectByKey.get(l.subjectKey)?.name || l.label }
    : { colorOf: (l) => gameByKey.get(l.gameKey)?.color || l.color, dashOf: () => [], labelOf: (l) => gameByKey.get(l.gameKey)?.label.short || l.label });

  const hasData = lines.some((l) => l.points.length > 0);

  // Legend: what the colors (and, on one chart, the line styles) mean
  const locationLegend = (
    <div className="an-legend-group">
      {subjects.map((s) => (
        <span key={s.key} className="rx-ov-legend-item">
          <span className="cx-ov-swatch" style={{ background: s.color }} />
          {s.name}
        </span>
      ))}
    </div>
  );
  const gameLegend = (withColor) => (
    <div className={`an-legend-group${split === 'none' ? ' an-legend-games' : ''}`}>
      {games.map((g) => (
        <span key={g.key} className="rx-ov-legend-item">
          {withColor ? <span className="cx-ov-swatch" style={{ background: g.color }} /> : <DashSample dash={g.dash} />}
          {g.label.short}
          {g.label.opponent && <span className="an-legend-sub"> · {g.label.opponent}</span>}
        </span>
      ))}
    </div>
  );

  return (
    <div className="graph panel-2 cx-chart-card rx-ov-chart an-chart">
      <ChartHeader
        title={title}
        subtitle={subtitle}
        controls={(
          <>
            {metricOptions.length > 1 && (
              <ChartSegmented options={metricOptions} value={metric} onChange={onMetric} />
            )}
            {extraControls}
            {onResolution && (
              <ChartSegmented
                label="Every"
                options={[
                  { value: 1, label: '1m' },
                  { value: 5, label: '5m' },
                  { value: 15, label: '15m' },
                  { value: 30, label: '30m' },
                  { value: 60, label: '1h' },
                ]}
                value={resolution}
                onChange={onResolution}
              />
            )}
            {!alignLocked && (
              <ChartSegmented
                label="Line up by"
                options={[{ value: 'start', label: 'Game start' }, { value: 'clock', label: 'Clock time' }]}
                value={align}
                onChange={onAlign}
              />
            )}
            {onSplitBy && splitOptions.length > 1 && (
              <ChartSegmented label="Split by" options={splitOptions} value={split} onChange={onSplitBy} />
            )}
          </>
        )}
      />
      <div className="an-legend">
        {split === 'location' ? gameLegend(true) : locationLegend}
        {split === 'none' && games.length > 1 && gameLegend(false)}
      </div>
      {alignLocked && (
        <div className="an-chart-note">Lined up by game start: averaged games have different start times.</div>
      )}
      {!hasData ? (
        <div style={{ position: 'relative', height: 360 }}>
          <div className="cx-empty">No data for the selected locations and games</div>
        </div>
      ) : split === 'none' ? (
        <div style={{ position: 'relative', height: 360 }}>
          <Line data={oneChartData} options={options} plugins={[hoverGuidePlugin]} />
        </div>
      ) : (
        <>
          <div className="an-chart-note">Same scale on every chart. {yTitle}.</div>
          <div className="an-panels">
            {panels.map((p) => (
              <div key={p.key} className="an-panel">
                <div className="an-panel-head">
                  {p.swatch}
                  <span className="an-panel-title">{p.title}</span>
                  {p.sub && <span className="an-panel-sub">{p.sub}</span>}
                </div>
                <div style={{ position: 'relative', height: 220 }}>
                  {p.lines.some((l) => l.points.length > 0) ? (
                    <Line data={panelData(p)} options={options} plugins={[hoverGuidePlugin]} />
                  ) : (
                    <div className="cx-empty">No data</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
